import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiGet } from "@/lib/api";

const SUPPORT_API = "/api/internal/support";

/**
 * The conversation list beside the chat.
 *
 * Until now a conversation became unreachable the moment it left the screen:
 * `GET /history` returned a summary and nothing more, and the only way back
 * into Lucas was to send a message. `GET /conversation/:id` and this hook are
 * the missing door.
 *
 * Reading a past conversation is deliberately read-only. Sending into it would
 * land in the *active* conversation instead — `getOrCreateConversation` always
 * resolves to the user's active one — and a message that silently goes
 * somewhere else is exactly the kind of quiet lie this screen keeps having to
 * remove.
 */
export function useLucasConversations({ messages = [] } = {}) {
  const [viewingId, setViewingId] = useState(null);
  const [viewed, setViewed] = useState(null);

  const historyQuery = useQuery({
    queryKey: ["support", "conversations"],
    queryFn: async () => {
      const res = await apiGet(`${SUPPORT_API}/history?limit=30`);
      if (!res.ok) return { conversations: [] };
      return res.json();
    },
    staleTime: 60_000,
  });

  const openConversation = useCallback(async (id) => {
    setViewingId(id);
    setViewed(null);
    const res = await apiGet(`${SUPPORT_API}/conversation/${id}`);
    if (res.ok) setViewed(await res.json());
  }, []);

  const resumeActive = useCallback(() => {
    setViewingId(null);
    setViewed(null);
  }, []);

  const deleteConversation = useCallback(
    async (id) => {
      const res = await apiDelete(`${SUPPORT_API}/conversation/${id}`);
      if (!res.ok) {
        toast.error("Não foi possível excluir a conversa.");
        return;
      }
      // Se a excluída era a que estava aberta, a tela precisa sair dela antes
      // do refetch — senão fica lendo uma conversa que não existe mais.
      setViewingId((current) => (current === id ? null : current));
      setViewed((current) => (viewingId === id ? null : current));
      historyQuery.refetch();
      toast.success("Conversa excluída.");
    },
    [historyQuery, viewingId],
  );

  /**
   * The active conversation has no stored title, so its first user message is
   * the closest thing to one — the same rule every chat product lands on.
   */
  const activeLabel = useMemo(() => {
    const first = messages.find((m) => m.role === "user" && m.content?.trim());
    if (!first) return null;
    const text = first.content.trim();
    return text.length > 60 ? `${text.slice(0, 60)}…` : text;
  }, [messages]);

  return {
    conversations: historyQuery.data?.conversations || [],
    refetchConversations: historyQuery.refetch,
    viewingId,
    isReadOnly: !!viewingId,
    isLoadingViewed: !!viewingId && !viewed,
    // While a past conversation is open it replaces the live one on screen.
    viewedMessages: viewed?.messages || [],
    activeLabel,
    hasActiveConversation: messages.length > 0,
    openConversation,
    resumeActive,
    deleteConversation,
  };
}

export default useLucasConversations;
